"use client";

import { useEffect, useRef, useState, type FormEvent } from "react";
import Link from "next/link";
import type { Route } from "next";
import { useRouter } from "next/navigation";
import { ShieldQuestion } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { StatusMessage } from "@/components/ui/status-message";
import { useSaveFeedback } from "@/lib/feedback/use-save-feedback";
import { useTranslation } from "@/lib/i18n";
import { bff, bffErrorBody } from "@/lib/api/bff";
import { ApiError } from "@/lib/api/client";

interface LoginFormProps {
  returnTo: string;
}

type Step = "credentials" | "twoFactor" | "enrollPrompt";

interface LoginResponseBody {
  outcome?: string;
  mustChangePassword?: boolean;
  twoFactorEnrollmentPending?: boolean;
  /** P0-1: `"operator"` marks a tenant-less hub principal (Betreiber-Kontext). */
  scope?: "tenant" | "operator";
}

/** Landing route of the Betreiber-Kontext — a hub operator has no tenant to land in. */
const OPERATOR_HOME = "/operator";

/**
 * Local credential login (hybrid-auth design §5): email/password against the
 * BFF (`POST /api/auth/local/login`); when the account has 2FA the form
 * advances to a code step (TOTP or recovery code) and resubmits. Outcomes map
 * to i18n error messages — uniform for invalid credentials (anti-enumeration),
 * distinct for lockout and invalid codes. Tokens never reach this component.
 *
 * Feedback level **E2**, deliberately (Action-Feedback-System §2.1/§2.3). A
 * rejected login leaves the form — and its submit button — standing, and the
 * user has to fix something *in it*, so the message belongs at the form and has
 * to persist until it is fixed. The root-layout toast host (§A6) finally makes
 * a toast possible on this page; that is not a reason to use one here.
 *
 * The feedback is bound to the STEP: advancing to the 2FA prompt, or going back
 * from it, is a different question being asked, so the previous answer's error
 * must not survive it (§A2). That is also why the „Zurück" button no longer
 * clears the message by hand.
 */
export function LoginForm({ returnTo }: LoginFormProps) {
  const { t } = useTranslation();
  const router = useRouter();

  const [step, setStep] = useState<Step>("credentials");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [pending, setPending] = useState(false);
  // No demotion timer: an unresolved login error is not something to soften
  // after eight seconds (§2.5 — a timer may demote emphasis, never remove it,
  // and there is nothing to demote to here).
  const feedback = useSaveFeedback(step, { demoteAfterMs: null });
  const error = feedback.state?.message ?? null;
  const errorRef = useRef<HTMLDivElement>(null);

  // §2.7: focus moves into the message on a submit failure. Without it the
  // keyboard/AT user is left standing on the submit button with an announcement
  // they cannot navigate back to. `feedback.state` is a fresh object per
  // failure, so a repeated identical error re-focuses (and re-announces) too.
  useEffect(() => {
    if (feedback.state) errorRef.current?.focus();
  }, [feedback.state]);

  function enter(target: string): void {
    // Full navigation via the router: the server components re-read the fresh session.
    router.push(target as Route);
    router.refresh();
  }

  async function submit(twoFactorCode?: string): Promise<void> {
    feedback.clear();
    setPending(true);
    try {
      // The route answers 401 for every negative outcome and puts the outcome
      // in the body, so a rejection is data, not an exception — `bffErrorBody`
      // unwraps it. `silentUnauthorized` stops that 401 from being read as an
      // expired session by the shell (backlog D19).
      let body: LoginResponseBody | null;
      try {
        body = await bff.post<LoginResponseBody>(
          "/api/auth/local/login",
          {
            email,
            password,
            ...(twoFactorCode ? { twoFactorCode } : {}),
          },
          { silentUnauthorized: true },
        );
      } catch (err) {
        body = bffErrorBody<LoginResponseBody>(err);
        // Not an HTTP response at all (offline, aborted) — no outcome to read.
        if (body === null && !(err instanceof ApiError)) throw err;
      }

      switch (body?.outcome) {
        case "success":
          if (body.mustChangePassword) {
            enter(`/auth/change-password?returnTo=${encodeURIComponent(returnTo)}`);
          } else if (body.twoFactorEnrollmentPending) {
            setStep("enrollPrompt");
          } else if (body.scope === "operator" && returnTo === "/") {
            // A hub operator holds no memberships, so the default landing route
            // would bounce them straight through the instance picker into an
            // arbitrary customer. Their home is the Betreiber-Kontext. An
            // explicit deep link still wins.
            enter(OPERATOR_HOME);
          } else {
            enter(returnTo);
          }
          return;
        case "two_factor_required":
          setStep("twoFactor");
          setCode("");
          return;
        case "two_factor_invalid":
          feedback.markError(t("auth.local.twoFactorInvalid"));
          return;
        case "locked_out":
          feedback.markError(t("auth.local.lockedOut"));
          return;
        case "invalid_credentials":
        case "disabled": // uniform message — no account-state enumeration
          feedback.markError(t("auth.local.invalidCredentials"));
          return;
        default:
          feedback.markError(t("auth.local.unavailable"));
      }
    } catch {
      feedback.markError(t("auth.local.unavailable"));
    } finally {
      setPending(false);
    }
  }

  function onCredentialsSubmit(event: FormEvent): void {
    event.preventDefault();
    void submit();
  }

  function onCodeSubmit(event: FormEvent): void {
    event.preventDefault();
    void submit(code.trim());
  }

  if (step === "enrollPrompt") {
    return (
      <div className="space-y-4" data-testid="enroll-prompt">
        <div className="flex items-start gap-3 rounded-md border border-border bg-muted/50 p-4">
          <ShieldQuestion className="mt-0.5 size-5 shrink-0 text-primary" aria-hidden />
          <div className="space-y-1">
            <p className="text-sm font-medium text-foreground">
              {t("auth.local.enrollPromptTitle")}
            </p>
            <p className="text-sm text-muted-foreground">
              {t("auth.local.enrollPromptDescription")}
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button asChild className="flex-1">
            <Link href="/settings/security">{t("auth.local.enrollNow")}</Link>
          </Button>
          <Button
            type="button"
            variant="outline"
            className="flex-1"
            onClick={() => enter(returnTo)}
            data-testid="enroll-later"
          >
            {t("auth.local.enrollLater")}
          </Button>
        </div>
      </div>
    );
  }

  if (step === "twoFactor") {
    return (
      <form onSubmit={onCodeSubmit} className="space-y-4" data-testid="two-factor-step">
        <div className="space-y-1">
          <h2 className="text-sm font-semibold text-foreground">
            {t("auth.local.twoFactorTitle")}
          </h2>
          <p className="text-sm text-muted-foreground">{t("auth.local.twoFactorDescription")}</p>
        </div>

        <div
          ref={errorRef}
          tabIndex={-1}
          className={error ? "outline-none" : "hidden"}
        >
          <StatusMessage tone="danger" className="text-sm" data-testid="login-error">
            {error}
          </StatusMessage>
        </div>

        <div className="space-y-2">
          <Label htmlFor="login-code">{t("auth.local.codeLabel")}</Label>
          <Input
            id="login-code"
            autoFocus
            autoComplete="one-time-code"
            inputMode="text"
            placeholder={t("auth.local.codePlaceholder")}
            value={code}
            onChange={(e) => setCode(e.target.value)}
            data-testid="code-input"
          />
          <p className="text-xs text-muted-foreground">{t("auth.local.recoveryHint")}</p>
        </div>

        <div className="flex gap-2">
          <Button
            type="button"
            variant="outline"
            disabled={pending}
            onClick={() => {
              // The step IS the feedback subject, so switching it clears the
              // message — no hand-written reset (§A2).
              setStep("credentials");
              setCode("");
            }}
          >
            {t("auth.local.back")}
          </Button>
          <Button
            type="submit"
            className="flex-1"
            loading={pending}
            disabled={code.trim().length === 0}
            data-testid="verify-code"
          >
            {t("auth.local.verify")}
          </Button>
        </div>
      </form>
    );
  }

  return (
    <form onSubmit={onCredentialsSubmit} className="space-y-4" data-testid="credentials-step">
      <div ref={errorRef} tabIndex={-1} className={error ? "outline-none" : "hidden"}>
        <StatusMessage tone="danger" className="text-sm" data-testid="login-error">
          {error}
        </StatusMessage>
      </div>

      <div className="space-y-2">
        <Label htmlFor="login-email">{t("auth.local.emailLabel")}</Label>
        <Input
          id="login-email"
          type="email"
          autoComplete="username"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          data-testid="email-input"
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="login-password">{t("auth.local.passwordLabel")}</Label>
        <Input
          id="login-password"
          type="password"
          autoComplete="current-password"
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          data-testid="password-input"
        />
      </div>

      {/* E0 (§2.2/§A7): `loading` keeps the name („Anmelden"), marks the control
          `aria-busy` and uses `aria-disabled`, so the keyboard focus stays on
          the button the user just pressed instead of falling to `<body>`. */}
      <Button
        type="submit"
        className="w-full"
        loading={pending}
        disabled={email.length === 0 || password.length === 0}
        data-testid="login-submit"
      >
        {t("auth.local.submit")}
      </Button>
    </form>
  );
}
